import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { PlatformAdminAddDialog } from "../components/PlatformAdminAddDialog";
import { SetPasswordDialog } from "../components/SetPasswordDialog";
import { UserPlus, ShieldCheck, Trash, Key, PaperPlaneTilt, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");

function StatusBadge({ a }) {
  if (a.invite_status === "pending") {
    return a.invite_expired
      ? <Badge className="text-[10px] bg-red-100 text-red-700" data-testid={`platform-admin-status-${a.id}`}>Invite expired {fmt(a.invite_expires_at)}</Badge>
      : <Badge className="text-[10px] bg-amber-100 text-amber-800" data-testid={`platform-admin-status-${a.id}`}>Invite pending · expires {fmt(a.invite_expires_at)}</Badge>;
  }
  if (a.active === false) return <Badge variant="secondary" className="text-[10px]" data-testid={`platform-admin-status-${a.id}`}>Inactive</Badge>;
  return <Badge className="text-[10px] bg-emerald-100 text-emerald-700" data-testid={`platform-admin-status-${a.id}`}>Active{a.force_password_change ? " · must change password" : ""}</Badge>;
}

export default function PlatformAdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const [busy, setBusy] = useState("");

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await API.get("/platform/admins");
      setAdmins(data.admins || []);
    } catch { toast.error("Failed to load platform admins"); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const resend = async (a) => {
    setBusy(`resend-${a.id}`);
    try {
      const { data } = await API.post(`/platform/admins/${a.id}/resend-invite`);
      const link = `${window.location.origin}${data.invite_path}`;
      let copied = false;
      try { await navigator.clipboard.writeText(link); copied = true; } catch { /* clipboard optional */ }
      toast.success(data.email_sent ? `New invite e-mailed${copied ? " — link copied too" : ""}` : `E-mail couldn't be sent${data.email_error ? ` (${data.email_error})` : ""} — ${copied ? "new link copied, send it to them" : `send them this link: ${link}`}`);
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend the invite"); }
    setBusy("");
  };

  const remove = async () => {
    if (!removeFor) return;
    setBusy("remove");
    try {
      const { data } = await API.delete(`/platform/admins/${removeFor.id}`);
      toast.success(data.message);
      setRemoveFor(null);
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove this admin"); }
    setBusy("");
  };

  if (loading) return <div className="flex items-center justify-center py-24"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="platform-admins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] flex items-center gap-2"><ShieldCheck size={28} className="text-primary" weight="duotone" /> Platform Admins</h1>
          <p className="text-sm text-muted-foreground mt-1">Who can run the Fixlr console. Invite by e-mail, or set a password and hand it over.</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="gap-2" data-testid="platform-admin-add-btn"><UserPlus size={18} /> Add admin</Button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr className="text-left">
              {["Name", "E-mail", "Status", "Last sign-in", "Added", ""].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {admins.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No platform admins found</td></tr>}
            {admins.map((a) => (
              <tr key={a.id} className="hover:bg-muted/50 transition-colors" data-testid={`platform-admin-row-${a.id}`}>
                <td className="px-4 py-3"><p className="text-sm font-medium">{a.name}{a.is_me && <span className="ml-1.5 text-[10px] text-muted-foreground">(you)</span>}</p>{!a.in_master && <p className="text-[11px] text-muted-foreground">also in {a.tenant_name}</p>}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{a.email}</td>
                <td className="px-4 py-3"><StatusBadge a={a} /></td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{a.last_active_at ? fmt(a.last_active_at) : "never"}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{fmt(a.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {a.invite_status === "pending" && (
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" disabled={busy === `resend-${a.id}`} onClick={() => resend(a)} data-testid={`platform-admin-resend-${a.id}`}>
                        <PaperPlaneTilt size={13} /> {a.invite_expired ? "New link" : "Resend"}
                      </Button>
                    )}
                    {!a.is_me && (
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setPwFor(a)} data-testid={`platform-admin-set-password-${a.id}`}><Key size={13} /> Set password</Button>
                    )}
                    {!a.is_me && (
                      <Button variant="ghost" size="sm" className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setRemoveFor(a)} data-testid={`platform-admin-remove-${a.id}`}><Trash size={15} /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PlatformAdminAddDialog open={showAdd} onClose={() => setShowAdd(false)} onAdded={fetchAdmins} />
      <SetPasswordDialog open={!!pwFor} onClose={() => setPwFor(null)} person={pwFor} endpoint={pwFor ? `/platform/admins/${pwFor.id}/set-password` : ""} onDone={fetchAdmins} />

      <Dialog open={!!removeFor} onOpenChange={(v) => { if (!v) setRemoveFor(null); }}>
        <DialogContent data-testid="platform-admin-remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><Trash size={20} /> Remove platform admin</DialogTitle>
            <DialogDescription>
              {removeFor?.in_master
                ? <>This deletes the login <strong>{removeFor?.email}</strong> completely — it only exists for the console.</>
                : <><strong>{removeFor?.email}</strong> loses platform access but keeps their login in {removeFor?.tenant_name}.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveFor(null)} data-testid="platform-admin-remove-cancel">Cancel</Button>
            <Button variant="destructive" onClick={remove} disabled={busy === "remove"} data-testid="platform-admin-remove-confirm"><Trash size={16} className="mr-2" /> {busy === "remove" ? "Removing…" : removeFor?.in_master ? "Delete login" : "Revoke access"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
